import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FaCheckCircle,
  FaCrown,
  FaExclamationTriangle,
  FaPen,
  FaSearch,
  FaShieldAlt,
  FaTimes,
  FaUser,
  FaUserPlus,
  FaUsers,
} from "react-icons/fa";

import UserForm from "./UserForm";

const roleLabels = {
  user: "Hausmeister",
  admin: "Administrator",
  superadmin: "Superadministrator",
};

const getDemoMode = () => {
  try {
    const token = localStorage.getItem("token");
    const segment = token?.split(".")[1];

    if (!segment) return false;

    const base64 = segment.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=");

    return JSON.parse(atob(padded)).environment === "demo";
  } catch {
    return false;
  }
};

const UserList = ({
  users = [],
  setUsers,
  currentRole = "user",
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [modal, setModal] = useState(null);
  const [message, setMessage] = useState(null);

  const dialogRef = useRef(null);
  const savingRef = useRef(false);

  const isDemo = getDemoMode();
  const canManage = ["admin", "superadmin"].includes(currentRole);

  const filteredUsers = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();

    return users.filter((user) =>
      [
        user.first_name,
        user.last_name,
        user.email,
        user.role,
        roleLabels[user.role],
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(search)
    );
  }, [users, searchTerm]);

  useEffect(() => {
    if (modal && dialogRef.current && !dialogRef.current.open) {
      dialogRef.current.showModal();
    }
  }, [modal]);

  const canEdit = (user) => {
    if (!canManage || user.can_edit === false) return false;
    if (isDemo) return user.role === "user";
    if (currentRole === "superadmin") return true;
    return user.role === "user";
  };

  const closeModal = () => {
    if (savingRef.current) return;

    dialogRef.current?.close();
    setModal(null);
  };

  const openModal = (user = null) => {
    if (!canManage || (user && !canEdit(user))) return;

    setMessage(null);
    setModal({ user });
  };

  return (
    <section className="user-list-section">
      <div className="user-list-heading">
        <div>
          <span>Kontenübersicht</span>
          <h3>Vorhandene Benutzer</h3>
        </div>

        <div className="user-list-heading-actions">
          <div className="user-list-count">
            <FaUsers />
            {users.length}
          </div>

          {canManage && (
            <button
              type="button"
              className="user-add-trigger"
              onClick={() => openModal()}
            >
              <FaUserPlus />
              Benutzer hinzufügen
            </button>
          )}
        </div>
      </div>

      {message && (
        <div
          className={`user-form-status user-form-status-${message.type}`}
          role={message.type === "error" ? "alert" : "status"}
        >
          {message.type === "success" ? (
            <FaCheckCircle />
          ) : (
            <FaExclamationTriangle />
          )}
          <span>{message.text}</span>
          <button
            type="button"
            onClick={() => setMessage(null)}
            aria-label="Meldung schließen"
          >
            <FaTimes />
          </button>
        </div>
      )}

      <div className="user-search">
        <FaSearch />
        <input
          type="search"
          placeholder="Name, E-Mail oder Rolle suchen …"
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          aria-label="Benutzer suchen"
        />
      </div>

      {filteredUsers.length === 0 ? (
        <div className="user-list-empty">
          <FaUsers />
          <h4>{users.length ? "Keine Treffer" : "Noch keine Benutzer"}</h4>
          <p>
            {users.length
              ? "Versuche einen anderen Suchbegriff."
              : "Lege über den Button einen neuen Benutzer an."}
          </p>
        </div>
      ) : (
        <div className="user-cards">
          {filteredUsers.map((user) => {
            const fullName = [user.first_name, user.last_name]
              .filter(Boolean)
              .join(" ")
              .trim();

            const role = ["user", "admin", "superadmin"].includes(user.role)
              ? user.role
              : "user";

            const icon =
              role === "superadmin" ? (
                <FaCrown />
              ) : role === "admin" ? (
                <FaShieldAlt />
              ) : (
                <FaUser />
              );

            return (
              <article key={user.id ?? user.email} className="user-card">
                <div className={`user-avatar user-avatar-${role}`}>
                  {icon}
                </div>

                <div className="user-card-content">
                  <strong>{fullName || user.email}</strong>
                  {fullName && (
                    <span className="user-card-email">{user.email}</span>
                  )}
                  <span className={`user-role-badge user-role-${role}`}>
                    {roleLabels[user.role] || user.role || "Unbekannte Rolle"}
                  </span>
                </div>

                {canEdit(user) && (
                  <button
                    type="button"
                    className="user-card-edit"
                    onClick={() => openModal(user)}
                    aria-label={`Benutzer ${fullName || user.email} bearbeiten`}
                    title="Benutzer bearbeiten"
                  >
                    <FaPen />
                  </button>
                )}
              </article>
            );
          })}
        </div>
      )}

      {modal && (
        <dialog
          ref={dialogRef}
          className="admin-modal user-account-modal"
          aria-labelledby="user-account-title"
          onCancel={(event) => {
            event.preventDefault();
            closeModal();
          }}
        >
          <UserForm
            users={users}
            setUsers={setUsers}
            currentRole={currentRole}
            user={modal.user}
            isDemo={isDemo}
            onClose={closeModal}
            onBusyChange={(busy) => {
              savingRef.current = busy;
            }}
            onSaved={(result) => {
              closeModal();
              setMessage(result);
            }}
          />
        </dialog>
      )}
    </section>
  );
};

export default UserList;